/**
 * The operational workspace stage body: story intro, the state surface that
 * leads it, the mutation status block, and the stage's regions. Split from the
 * root so neither file crosses the size gate.
 */
import React from "react";
import { FmTabsPanel } from "./FmTabs";
import { FmButton } from "./FmButton";
import { FmHeading, FmText } from "./FmComposition";
import { FmSplit } from "./FmLayout";
import { FmKeyFigures, type FmKeyFigure } from "./FmKeyFigures";
import { FmProgress, FmSpinner } from "./FmFeedback";
import { OperationalWorkspaceRegion } from "./FmOperationalWorkspaceRegions";
import { GuidedStepHeader, GuidedStepOutcome } from "./FmOperationalWorkspaceGuidance";
import { StageTransition } from "./FmOperationalWorkspaceStageFlow";
import {
  PaidBoundarySurface,
  RegionStateSurface,
  StateAction,
  WorkspaceLeadingStateSurface,
  type OperationalActionActivate,
} from "./FmOperationalWorkspaceStateSurfaces";
import {
  actionClick,
  operationalWorkspaceStatus,
  paidBoundariesFor,
  regionStateKind,
  rendersRegions,
  stageRegionPlan,
  regionNoticeActionIds,
  stateOwnedActionIds,
  stateSurfaceActionId,
} from "./FmOperationalWorkspaceStatus";
import type {
  FmOperationalActionDescriptor,
  FmOperationalStageDescriptor,
  FmOperationalWorkspaceDescriptor,
} from "./FmOperationalWorkspaceRoot.types";

const RECEIPT_GLYPH = {
  blocker: "!",
  success: "\u2713",
  information: "i",
} as const;

function activeToolbarControls(
  descriptor: FmOperationalWorkspaceDescriptor,
  activeStage: FmOperationalStageDescriptor,
): FmOperationalActionDescriptor[] {
  const activeControlIds = new Set(activeStage.controlIds);
  const stateOwned = stateOwnedActionIds(descriptor);
  const noticeOwned = regionNoticeActionIds(descriptor, activeStage);
  const alreadyOpenDialogActions = new Set(
    descriptor.dialogs
      .filter((dialog) => dialog.open)
      .map((dialog) => dialog.actionId),
  );
  return descriptor.controls.filter(
    (action) =>
      activeControlIds.has(action.id) &&
      !stateOwned.has(action.id) &&
      !noticeOwned.has(action.id) &&
      !alreadyOpenDialogActions.has(action.id),
  );
}

function WorkspaceControls({
  controls,
  descriptor,
  activeStage,
  inhibited,
  inhibitionReason,
  dialogTriggerRef,
  setOpenDialogId,
}: {
  controls: readonly FmOperationalActionDescriptor[];
  descriptor: FmOperationalWorkspaceDescriptor;
  activeStage: FmOperationalStageDescriptor;
  inhibited: boolean;
  inhibitionReason: string | null;
  dialogTriggerRef: React.RefObject<HTMLButtonElement | null>;
  setOpenDialogId: (id: string | null) => void;
}): React.ReactElement | null {
  if (!controls.length) return null;
  return (
    <div
      className="fm-operational-workspace__controls"
      role="toolbar"
      aria-label={activeStage.label}
    >
      {controls.map((action) => {
        const actionInhibited = inhibited && !action.availableWhenUnavailable;
        return (
          <FmButton
            key={action.id}
            variant={action.variant ?? "default"}
            size={action.size}
            danger={action.tone === "destructive"}
            data-tone={action.tone}
            disabled={action.disabled || actionInhibited}
            title={
              action.disabledReason ??
              (actionInhibited ? (inhibitionReason ?? undefined) : undefined)
            }
            onClick={(event) => {
              dialogTriggerRef.current = event.currentTarget;
              actionClick(action, descriptor, setOpenDialogId, actionInhibited);
            }}
            data-testid={`operational-action-${action.id}`}
          >
            {action.label}
          </FmButton>
        );
      })}
    </div>
  );
}

function WorkspaceStatus({
  descriptor,
  onActivate,
}: {
  descriptor: FmOperationalWorkspaceDescriptor;
  onActivate: OperationalActionActivate;
}): React.ReactElement | null {
  const { receipts } = descriptor.mutation;
  if (descriptor.mutation.status === "idle" && !receipts.length) return null;
  // A settled outcome with nothing to add is already the header's status rail;
  // printing the same words again above the controls makes one fact read as two.
  if (
    descriptor.mutation.status !== "pending" &&
    !receipts.length &&
    operationalWorkspaceStatus(descriptor).label === descriptor.mutation.label
  )
    return null;
  // One receipt takes the short mutation label as its title and keeps its
  // sentence as body text, so the label is not printed again above it.
  const receiptTitle = receipts.length === 1 ? descriptor.mutation.label : null;
  return (
    <div className="fm-operational-workspace__status" aria-live="polite">
      {receiptTitle === null ? (
        <FmText size="caption" tone="muted">
          {descriptor.mutation.label}
        </FmText>
      ) : null}
      {descriptor.mutation.status === "pending" ? (
        descriptor.mutation.progress === undefined ? (
          <FmSpinner size="sm" />
        ) : (
          <FmProgress percent={descriptor.mutation.progress} size="sm" />
        )
      ) : null}
      {receipts.map((receipt) => (
        <div
          key={receipt.id}
          className="fm-operational-workspace__receipt"
          data-kind={receipt.kind}
          /* The machine code stays a test/telemetry attribute; owners read the
             message and act on the resolving control (6wbfb.6.20). */
          data-code={receipt.code}
          role={receipt.kind === "blocker" ? "alert" : "status"}
        >
          <span
            className="fm-operational-workspace__receipt-glyph"
            aria-hidden="true"
          >
            {RECEIPT_GLYPH[receipt.kind]}
          </span>
          <div className="fm-operational-workspace__receipt-body">
            {receiptTitle !== null ? <strong>{receiptTitle}</strong> : null}
            <p>{receipt.message}</p>
            {/* One resolving control, rendered once: when the state surface
                already offers this exact action, the receipt does not repeat it
                (a duplicate would share its test id). */}
            {receipt.kind === "blocker" &&
            receipt.resolvingActionId !== stateSurfaceActionId(descriptor) ? (
              <StateAction
                descriptor={descriptor}
                actionId={receipt.resolvingActionId}
                onActivate={onActivate}
              />
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

/** The stage's answer figure at the statement tier, directly under the question. */
function StageAnswerFigure({
  figure,
}: {
  figure: NonNullable<FmOperationalStageDescriptor["answerFigure"]>;
}): React.ReactElement {
  const base = {
    id: figure.id,
    label: figure.label,
    testId: `operational-answer-figure-${figure.id}`,
  };
  const item: FmKeyFigure =
    figure.value === null
      ? { ...base, value: null, unavailableReason: figure.caption }
      : {
          ...base,
          value: figure.value,
          ...(figure.unit ? { unit: figure.unit } : {}),
          ...(figure.caption ? { footnote: figure.caption } : {}),
        };
  return <FmKeyFigures presentation="rail" scale="display" items={[item]} />;
}

/**
 * Futures and Plan lead with their governed lens-selection evidence. Confirm
 * leads with its commit facts. Repeating the generic stage prose above those
 * surfaces creates a second banner or an empty band before the actual task.
 */
function stageShowsStory(stage: FmOperationalStageDescriptor): boolean {
  return !["futures", "plan", "confirm"].includes(stage.id);
}

/**
 * A guided step whose work is one region asks its question as that region's
 * heading: printing the region title under the question gave the step two
 * headings for one piece of work.
 */
function stepHeadsRegion(
  descriptor: FmOperationalWorkspaceDescriptor,
  activeStage: FmOperationalStageDescriptor,
  stageRegions: readonly unknown[],
): boolean {
  return Boolean(descriptor.guidance) && stageRegions.length === 1 && stageShowsStory(activeStage);
}

function WorkspaceStoryIntro({
  descriptor,
  activeStage,
  toolbarControls,
  inhibited,
  inhibitionReason,
  dialogTriggerRef,
  setOpenDialogId,
  onActivate,
}: {
  descriptor: FmOperationalWorkspaceDescriptor;
  activeStage: FmOperationalStageDescriptor;
  toolbarControls: readonly FmOperationalActionDescriptor[];
  inhibited: boolean;
  inhibitionReason: string | null;
  dialogTriggerRef: React.RefObject<HTMLButtonElement | null>;
  setOpenDialogId: (id: string | null) => void;
  onActivate: OperationalActionActivate;
}): React.ReactElement | null {
  const isReady = descriptor.state === "ready";
  const banners = isReady ? descriptor.banners : [];
  const hasStatus =
    descriptor.mutation.status !== "idle" ||
    descriptor.mutation.receipts.length > 0;
  const showStageStory = stageShowsStory(activeStage);
  if (
    !showStageStory &&
    !banners.length &&
    !hasStatus &&
    !toolbarControls.length
  )
    return null;
  const aside = (
    <div className="fm-operational-workspace__story-aside">
      {banners.map((banner) => (
        <aside
          key={banner.id}
          className="fm-operational-workspace__notice"
          data-tone={banner.tone}
          data-testid={`operational-banner-${banner.id}`}
        >
          <strong>{banner.title}</strong>
          {banner.description ? <span>{banner.description}</span> : null}
        </aside>
      ))}
      <WorkspaceStatus descriptor={descriptor} onActivate={onActivate} />
      <WorkspaceControls
        controls={toolbarControls}
        descriptor={descriptor}
        activeStage={activeStage}
        inhibited={inhibited}
        inhibitionReason={inhibitionReason}
        dialogTriggerRef={dialogTriggerRef}
        setOpenDialogId={setOpenDialogId}
      />
    </div>
  );
  return (
    <div
      className="fm-operational-workspace__story-intro"
      data-utility-only={showStageStory ? undefined : ""}
      data-conversion-role={activeStage.conversionRole}
    >
      {showStageStory ? (
        <FmSplit
          balance="copy-narrow"
          side="start"
          stackOrder="graphic-first"
          contentSurface="plain"
          graphic={
            <div className="fm-operational-workspace__story-copy">
              {/* A guided flow's progress line already names the step. */}
              {descriptor.guidance ? null : (
                <span className="fm-operational-workspace__eyebrow">
                  {activeStage.label}
                </span>
              )}
              <FmHeading level={2}>{activeStage.ownerQuestion}</FmHeading>
              {/* The figure answers the question, so it follows it directly and
                  the prose reading comes after; on a phone the prose first
                  pushed the figure under the fold. */}
              {isReady && activeStage.answerFigure ? (
                <StageAnswerFigure figure={activeStage.answerFigure} />
              ) : null}
              <p className="fm-operational-workspace__literal-answer">
                {isReady ? activeStage.literalAnswer : descriptor.stateLabel}
              </p>
            </div>
          }
          content={aside}
        />
      ) : (
        aside
      )}
    </div>
  );
}

function stageBodyControls(
  descriptor: FmOperationalWorkspaceDescriptor,
  activeStage: FmOperationalStageDescriptor,
): {
  commitControls: FmOperationalActionDescriptor[];
  introControls: FmOperationalActionDescriptor[];
} {
  const toolbarControls = activeToolbarControls(descriptor, activeStage);
  const commits = activeStage.conversionRole === "commit";
  return {
    commitControls: commits ? toolbarControls : [],
    introControls: commits ? [] : toolbarControls,
  };
}

/** The stage body: story intro, the state surface that leads it, and regions. */
export function WorkspaceStageBody({
  descriptor,
  activeStage,
  activeStageId,
  tier,
  dataTestId,
  bodyTestId,
  stageContentRef,
  dialogTriggerRef,
  setOpenDialogId,
  onActivate,
  onStageChange,
  inhibited,
  inhibitionReason,
}: {
  descriptor: FmOperationalWorkspaceDescriptor;
  activeStage: FmOperationalStageDescriptor;
  activeStageId: string;
  tier: string;
  dataTestId: string;
  bodyTestId: string;
  stageContentRef: React.RefObject<HTMLElement | null>;
  dialogTriggerRef: React.RefObject<HTMLButtonElement | null>;
  setOpenDialogId: (id: string | null) => void;
  onActivate: OperationalActionActivate;
  onStageChange: (stageId: string) => void;
  inhibited: boolean;
  inhibitionReason: string | null;
}): React.ReactElement {
  const { commitControls, introControls } = stageBodyControls(
    descriptor,
    activeStage,
  );
  const { stageRegions, outcomeAfterRegionId } = stageRegionPlan(descriptor, activeStage, tier);
  const headedByStep = stepHeadsRegion(descriptor, activeStage, stageRegions);
  /* The panel of the Radix tab set rooted in OperationalWorkspaceFrame. Only the
     active stage has a panel, and it keeps the stage anchor id its tab links to. */
  return (
    <FmTabsPanel value={activeStageId} asChild>
    <section
      ref={stageContentRef}
      id={`${descriptor.id}-${activeStageId}`}
      tabIndex={-1}
      className="fm-operational-workspace__body"
      data-testid={bodyTestId}
      data-stage-content={activeStageId}
      aria-labelledby={`${descriptor.id}-${activeStageId}-tab`}
    >
      {descriptor.guidance ? (
        <GuidedStepHeader
          guidance={descriptor.guidance}
          stages={descriptor.stages}
          activeStage={activeStage}
          testId={dataTestId}
          onStageChange={onStageChange}
        />
      ) : null}
      <WorkspaceStoryIntro
        descriptor={descriptor}
        activeStage={activeStage}
        toolbarControls={introControls}
        inhibited={inhibited}
        inhibitionReason={inhibitionReason}
        dialogTriggerRef={dialogTriggerRef}
        setOpenDialogId={setOpenDialogId}
        onActivate={onActivate}
      />
      <WorkspaceLeadingStateSurface
        descriptor={descriptor}
        workspaceTestId={dataTestId}
        activeStageId={activeStage.id}
        onActivate={onActivate}
      />
      <PaidBoundarySurface
        descriptor={descriptor}
        boundaries={paidBoundariesFor(descriptor, {
          kind: "leading",
          activeStageId: activeStage.id,
        })}
        onActivate={onActivate}
        leading
      />
      {stageRegions.map((region, index) => (
              <React.Fragment key={region.id}>
                <OperationalWorkspaceRegion
                  descriptor={descriptor}
                  region={region}
                  dominant={region.id === activeStage.dominantRegionId}
                  headedByStep={headedByStep}
                  inhibitionReason={inhibitionReason}
                  onActivate={onActivate}
                  paidBoundary={
                    <PaidBoundarySurface
                      descriptor={descriptor}
                      boundaries={paidBoundariesFor(descriptor, {
                        kind: "region",
                        regionId: region.id,
                      })}
                      onActivate={onActivate}
                    />
                  }
                  stateSurface={
                    regionStateKind(descriptor, region.id) ? (
                      <RegionStateSurface
                        descriptor={descriptor}
                        regionId={region.id}
                        workspaceTestId={dataTestId}
                        onActivate={onActivate}
                      />
                    ) : null
                  }
                  stateSurfaceMode={
                    regionStateKind(descriptor, region.id) === "error"
                      ? "replace"
                      : "lead"
                  }
                />
                {index === 0 && commitControls.length ? (
                  <div className="fm-operational-workspace__commit-rail">
                    <WorkspaceControls
                      controls={commitControls}
                      descriptor={descriptor}
                      activeStage={activeStage}
                      inhibited={inhibited}
                      inhibitionReason={inhibitionReason}
                      dialogTriggerRef={dialogTriggerRef}
                      setOpenDialogId={setOpenDialogId}
                    />
                  </div>
                ) : null}
                {descriptor.guidance && region.id === outcomeAfterRegionId ? (
                  <GuidedStepOutcome guidance={descriptor.guidance} testId={dataTestId} />
                ) : null}
              </React.Fragment>
            ))}
      {rendersRegions(descriptor) ? (
        <StageTransition
          descriptor={descriptor}
          activeStage={activeStage}
          testId={dataTestId}
          outcomePlaced={outcomeAfterRegionId !== null}
          onStageChange={onStageChange}
        />
      ) : null}
    </section>
    </FmTabsPanel>
  );
}
